<template>
  <div class="vff-animate q-form" v-bind:class="mainClasses" ref="qanimate">
    <div class="q-inner">
      <div
        v-bind:class="{
          'f-section-wrap': question.type === QuestionType.SectionBreak
        }"
      >
        <div
          v-bind:class="{ fh4: question.type !== QuestionType.SectionBreak }"
        >
          <span class="f-tagline" v-if="question.tagline">{{
            question.tagline
          }}</span>
          <div
            v-if="question.type === QuestionType.StartPage && question.imgUrl"
            class="image-wrapper"
          >
            <img :src="question.imgUrl" />
          </div>
          <template>
            <span class="fh4" v-if="question.type === QuestionType.SectionBreak"
              >{{ question.title }}
            </span>
            <span
              v-else
              class="f-text f-title-color"
              :class="{
                'text-center':
                  question.type === QuestionType.StartPage ||
                  question.type === QuestionType.EndPage ||
                  question.type === QuestionType.MultipleChoiceQuiz ||
                  question.type === QuestionType.TrueFalse
              }"
            >
              <span>{{ question.title }}</span>
              <span
                v-if="
                  question.type === QuestionType.MultipleChoiceQuiz ||
                    question.type === QuestionType.TrueFalse
                "
                v-html="question.description"
              ></span>
              <span v-if="!question.title">...</span>
              <!-- Required questions are marked by an asterisk (*) -->
              <span
                v-if="question.required"
                v-bind:aria-label="language.ariaRequired"
                role="note"
                ><span> *</span>
              </span>
              <span v-if="question.inline" class="f-answer flex-center">
                <component
                  ref="questionComponent"
                  v-bind:is="question.type"
                  v-bind:submitted="submitted"
                  v-bind:question="question"
                  v-bind:language="language"
                  v-model="dataValue"
                  v-bind:active="active"
                  v-on:next="onEnter"
                  v-on:submit="submit()"
                />
              </span>
            </span>
          </template>

          <span class="f-sub text-center" v-if="showHelperText">
            <span v-if="question.subtitle">{{ question.subtitle }}</span>
          </span>
        </div>
        <div
          v-if="
            question.type !== QuestionType.MultipleChoiceQuiz &&
              question.type !== QuestionType.TrueFalse
          "
        >
          <div
            v-if="question.description || question.descriptionLink.length !== 0"
            class="f-description"
            :class="{
              'text-center':
                question.type === QuestionType.StartPage ||
                question.type === QuestionType.EndPage
            }"
          >
            <span v-html="question.description" v-if="question.description" />
            <a
              v-for="(link, index) in question.descriptionLink"
              class="f-link"
              v-bind:key="'m' + index"
              v-bind:href="link.url"
              v-bind:target="link.target"
              >{{ link.text || link.url }}</a
            >
          </div>
        </div>
        <div v-if="!question.inline" class="f-answer f-full-width">
          <component
            ref="questionComponent"
            v-bind:is="question.type"
            v-bind:submitted="submitted"
            v-bind:question="question"
            v-bind:language="language"
            v-model="dataValue"
            v-bind:active="active"
            v-bind:disable-answer="disableAnswer"
            v-on:next="onEnter"
            v-on:submit="submit()"
          />
        </div>
      </div>
      <div
        class="vff-animate f-fade-in f-enter flex-center"
        v-if="showOkButton()"
      >
        <button
          class="o-btn-action"
          type="button"
          ref="button"
          href="#"
          v-on:click.prevent="onEnter"
          v-bind:aria-label="language.ariaOk"
        >
          <span v-if="question.type === QuestionType.StartPage && !isQuiz">{{
            question.btnText ? question.btnText : language.start
          }}</span>
          <span v-else-if="question.type === QuestionType.SectionBreak">{{
            language.continue
          }}</span>
          <span v-else-if="isQuiz">{{
            question.btnText ? question.btnText : $t("message.app.startQuiz")
          }}</span>
        </button>
      </div>

      <div
        v-if="showInvalid()"
        class="f-invalid"
        role="alert"
        aria-live="assertive"
      >
        {{ language.invalidPrompt }}
      </div>
    </div>
  </div>
</template>

<script>
import LanguageModel from "../models/LanguageModel";
import QuestionModel, {
  QuestionType,
  LinkOption
} from "../models/QuestionModel";
import DropdownType from "./QuestionTypes/DropdownType.vue";
import DropdownDateType from "./QuestionTypes/DropdownDateType.vue";
import EmailType from "./QuestionTypes/EmailType.vue";
import LongTextType from "./QuestionTypes/LongTextType.vue";
import ShortTextType from "./QuestionTypes/ShortTextType.vue";
import MultipleChoiceType from "./QuestionTypes/MultipleChoiceType.vue";
import NumberType from "./QuestionTypes/NumberType.vue";
import PasswordType from "./QuestionTypes/PasswordType.vue";
import PhoneType from "./QuestionTypes/PhoneType.vue";
import SectionBreakType from "./QuestionTypes/SectionBreakType.vue";
import TextType from "./QuestionTypes/TextType.vue";
import UrlType from "./QuestionTypes/UrlType.vue";
import DateType from "./QuestionTypes/DateType.vue";
import StartPageType from "./QuestionTypes/StartPageType.vue";
import StatementType from "./QuestionTypes/StatementType.vue";
import ScaleType from "./QuestionTypes/ScaleType.vue";
import EndPageType from "./QuestionTypes/EndPageType.vue";
import FileUploadType from "./QuestionTypes/FileUploadType.vue";
import MultipleChoiceQuizType from "./QuestionTypes/MultipleChoiceQuizType.vue";
import TrueFalseType from "./QuestionTypes/TrueFalseType.vue";
import { IsMobile } from "../mixins/IsMobile";

export default {
  name: "FlowFormQuestion",
  components: {
    StartPageType,
    StatementType,
    ScaleType,
    EndPageType,
    DateType,
    DropdownType,
    DropdownDateType,
    EmailType,
    LongTextType,
    ShortTextType,
    MultipleChoiceType,
    NumberType,
    PasswordType,
    PhoneType,
    SectionBreakType,
    TextType,
    UrlType,
    FileUploadType,
    MultipleChoiceQuizType,
    TrueFalseType
  },
  props: {
    submitted: {
      type: Boolean
    },
    question: QuestionModel,
    language: LanguageModel,
    value: [String, Array, Boolean, Number],
    active: {
      type: Boolean,
      default: false
    },
    reverse: {
      type: Boolean,
      default: false
    },
    isQuiz: {
      type: Boolean,
      default: false
    },
    disableAnswer: {
      type: Boolean,
      default: false
    }
  },
  mixins: [IsMobile],
  data() {
    return {
      QuestionType: QuestionType,
      dataValue: null
    };
  },
  mounted() {
    this.focusField();
    this.dataValue = this.question.answer;

    this.$refs.qanimate.addEventListener("animationend", this.onAnimationEnd);
  },
  beforeDestroy() {
    this.$refs.qanimate.removeEventListener(
      "animationend",
      this.onAnimationEnd
    );
  },
  methods: {
    /**
     * Autofocus the input box of the current question
     */
    focusField() {
      const el = this.$refs.questionComponent;

      el && el.focus();
    },

    onAnimationEnd() {
      this.focusField();
    },

    shouldFocus() {
      const q = this.$refs.questionComponent;

      return q && q.canReceiveFocus && !q.focused;
    },

    returnFocus() {
      const q = this.$refs.questionComponent;

      if (this.shouldFocus()) {
        this.focusField();
      }
    },

    /**
     * Emits "answer" event and calls "onEnter" method on Enter press
     */

    onEnter($event) {
      const q = this.$refs.questionComponent;

      if (q) {
        if (!q.focused) {
          this.$emit("answer", q);
        }

        q.onEnter();
      }
    },

    onTab($event) {
      const q = this.$refs.questionComponent;

      if (q && this.question.type !== QuestionType.Date) {
        this.returnFocus();
        this.$emit("answer", q);

        q.onEnter();
      }
    },

    /**
     * Check if the "OK" button should be shown.
     */
    showOkButton() {
      const q = this.$refs.questionComponent;

      if (this.question.type === QuestionType.StartPage) {
        return this.active;
      }

      if (this.question.allowOther && this.question.other) {
        return true;
      }

      if (QuestionType.MultipleChoice && QuestionType.Dropdown) {
        return false;
      }

      if (!q || !this.dataValue) {
        return false;
      }

      return q.hasValue && q.isValid();
    },

    /**
     * Determins if the invalid message should be shown.
     */
    showInvalid() {
      const q = this.$refs.questionComponent;

      if (!q || !this.dataValue) {
        return false;
      }

      return q.showInvalid();
    },

    submit() {
      this.$emit("submit");
    }
  },
  computed: {
    mainClasses: {
      cache: false,
      get() {
        const classes = {
          "q-is-active": this.active,
          "q-is-inactive": !this.active,
          "f-fade-in-down": this.reverse,
          "f-fade-in-up": !this.reverse,
          "f-focused":
            this.$refs.questionComponent &&
            this.$refs.questionComponent.focused,
          "f-has-value":
            this.$refs.questionComponent &&
            this.$refs.questionComponent.hasValue
        };

        classes[
          "field-" + this.question.type.toLowerCase().substring(8)
        ] = true;

        return classes;
      }
    },

    showHelperText() {
      if (this.question.subtitle) {
        return true;
      }

      if (
        this.question.type === QuestionType.LongText ||
        this.question.type === QuestionType.MultipleChoice
      ) {
        return this.question.helpTextShow;
      }

      return false;
    }
  }
};
</script>

<style scoped>
.image-wrapper {
  width: 100%;
  max-width: 600px;
  height: auto;
  margin: auto;
}

.image-wrapper img {
  margin: auto;
  margin-bottom: 8px;
  display: block;
}
</style>
